import { BaseApiClient } from './base-client'
import {
  API_ROUTES,
  interpolateRoute,
  ApiPaginatedResponse,
  ApiResponse,
} from '@super/shared-types'
import type {
  ThreadEvent,
  PaginatedThreadEventsResponse,
  SessionAroundResponse,
  GetThreadEventsParams,
  ThreadEventSearchResponse,
} from '@super/shared-types'

/**
 * Thread Events API Client
 * Handles fetching thread activity events for the chat interface
 * Supports both regular users and super admins
 */
export class ThreadEventsApiClient extends BaseApiClient {
  /**
   * Get thread events with pagination
   */
  async getThreadEvents(
    threadId: string,
    params?: GetThreadEventsParams,
  ): Promise<PaginatedThreadEventsResponse> {
    const queryString = params ? this.buildQueryString(params) : ''
    const route = interpolateRoute(API_ROUTES.THREAD_EVENTS.GET_BY_THREAD, {
      threadId,
    })
    const response = await this.request<ApiPaginatedResponse<ThreadEvent>>(
      `${route}${queryString}`,
    )
    return { data: response.data, pagination: response.pagination }
  }

  /**
   * Get older thread events (before cursor)
   */
  async getThreadEventsBefore(
    threadId: string,
    cursor: string,
    limit = 20,
  ): Promise<PaginatedThreadEventsResponse> {
    const route = interpolateRoute(API_ROUTES.THREAD_EVENTS.GET_BEFORE_CURSOR, {
      threadId,
      cursor,
    })
    const response = await this.request<ApiPaginatedResponse<ThreadEvent>>(
      `${route}?limit=${limit}`,
    )
    return { data: response.data, pagination: response.pagination }
  }

  /**
   * Get thread events around a specific message
   * Loads half page before and half page after the target message
   * Used for deeplinks to jump directly to a message
   */
  async getThreadEventsAroundMessage(
    threadId: string,
    eventId: string,
    limit = 50,
  ): Promise<PaginatedThreadEventsResponse> {
    const route = interpolateRoute(
      API_ROUTES.THREAD_EVENTS.GET_AROUND_MESSAGE,
      { threadId, eventId },
    )
    const response = await this.request<ApiPaginatedResponse<ThreadEvent>>(
      `${route}?limit=${limit}`,
    )
    return { data: response.data, pagination: response.pagination }
  }

  /**
   * Get thread events around the first event of a session.
   * Used for sessionId-based deeplinks.
   */
  async getThreadEventsAroundSession(
    threadId: string,
    sessionId: string,
    limit = 50,
  ): Promise<SessionAroundResponse> {
    const route = interpolateRoute(
      API_ROUTES.THREAD_EVENTS.GET_AROUND_SESSION,
      { threadId, sessionId },
    )
    return this.request<SessionAroundResponse>(`${route}?limit=${limit}`)
  }

  /**
   * Get contact activity across all threads
   */
  async getContactActivity(
    companyId: string,
    contactId: string,
    params?: GetThreadEventsParams,
  ): Promise<PaginatedThreadEventsResponse> {
    const queryString = params ? this.buildQueryString(params) : ''
    const route = interpolateRoute(API_ROUTES.THREAD_EVENTS.GET_BY_CONTACT, {
      companyId,
      contactId,
    })
    const response = await this.request<ApiPaginatedResponse<ThreadEvent>>(
      `${route}${queryString}`,
    )
    return { data: response.data, pagination: response.pagination }
  }

  /**
   * Get a single thread event by ID
   */
  async getThreadEventById(eventId: string): Promise<ThreadEvent> {
    const route = interpolateRoute(API_ROUTES.THREAD_EVENTS.GET_BY_ID, {
      id: eventId,
    })
    const response = await this.request<ApiResponse<ThreadEvent>>(route)
    return response.data!
  }

  /**
   * Get events by correlation ID
   */
  async getEventsByCorrelation(correlationId: string): Promise<ThreadEvent[]> {
    const route = interpolateRoute(
      API_ROUTES.THREAD_EVENTS.GET_BY_CORRELATION,
      { correlationId },
    )
    const response = await this.request<ApiResponse<ThreadEvent[]>>(route)
    return response.data!
  }

  /**
   * Search thread events using BM25 full-text search
   * Returns results grouped by session
   */
  async searchThreadEvents(params: {
    q: string
    limit?: number
    directions?: string
    channels?: string
    dateFrom?: string
    dateTo?: string
    sortBy?: 'asc' | 'desc'
  }): Promise<ThreadEventSearchResponse> {
    const queryString = this.buildQueryString(params)
    return this.request<ThreadEventSearchResponse>(
      `${API_ROUTES.THREAD_EVENTS.SEARCH}${queryString}`,
    )
  }

  // Admin methods (require super admin privileges)

  /**
   * [ADMIN] Get thread events with pagination for any company
   */
  async getAdminThreadEvents(
    threadId: string,
    params?: GetThreadEventsParams & { companyId?: string },
  ): Promise<PaginatedThreadEventsResponse> {
    const queryString = params ? this.buildQueryString(params) : ''
    const route = interpolateRoute(
      API_ROUTES.ADMIN.THREAD_EVENTS.GET_BY_THREAD,
      { threadId },
    )
    const response = await this.request<ApiPaginatedResponse<ThreadEvent>>(
      `${route}${queryString}`,
    )
    return { data: response.data, pagination: response.pagination }
  }

  /**
   * [ADMIN] Get older thread events (before cursor) for any company
   */
  async getAdminThreadEventsBefore(
    threadId: string,
    cursor: string,
    limit = 20,
    companyId?: string,
  ): Promise<PaginatedThreadEventsResponse> {
    const route = interpolateRoute(
      API_ROUTES.ADMIN.THREAD_EVENTS.GET_BEFORE_CURSOR,
      { threadId, cursor },
    )
    const queryString = companyId
      ? `?companyId=${companyId}&limit=${limit}`
      : `?limit=${limit}`
    const response = await this.request<ApiPaginatedResponse<ThreadEvent>>(
      `${route}${queryString}`,
    )
    return { data: response.data, pagination: response.pagination }
  }

  /**
   * [ADMIN] Get thread events around a specific message for any company
   * Loads half page before and half page after the target message
   * Used for deeplinks to jump directly to a message
   */
  async getAdminThreadEventsAroundMessage(
    threadId: string,
    eventId: string,
    limit = 50,
    companyId?: string,
  ): Promise<PaginatedThreadEventsResponse> {
    const route = interpolateRoute(
      API_ROUTES.ADMIN.THREAD_EVENTS.GET_AROUND_MESSAGE,
      { threadId, eventId },
    )
    const queryString = companyId
      ? `?companyId=${companyId}&limit=${limit}`
      : `?limit=${limit}`
    const response = await this.request<ApiPaginatedResponse<ThreadEvent>>(
      `${route}${queryString}`,
    )
    return { data: response.data, pagination: response.pagination }
  }

  /**
   * [ADMIN] Get thread events around the first event of a session for any company.
   * Used for sessionId-based deeplinks (SuperAdmin).
   */
  async getAdminThreadEventsAroundSession(
    threadId: string,
    sessionId: string,
    limit = 50,
    companyId?: string,
  ): Promise<SessionAroundResponse> {
    const route = interpolateRoute(
      API_ROUTES.ADMIN.THREAD_EVENTS.GET_AROUND_SESSION,
      { threadId, sessionId },
    )
    const queryString = companyId
      ? `?companyId=${companyId}&limit=${limit}`
      : `?limit=${limit}`
    return this.request<SessionAroundResponse>(`${route}${queryString}`)
  }

  /**
   * [ADMIN] Get contact activity across all threads for any company
   */
  async getAdminContactActivity(
    companyId: string,
    contactId: string,
    params?: GetThreadEventsParams,
  ): Promise<PaginatedThreadEventsResponse> {
    const queryString = params ? this.buildQueryString(params) : ''
    const route = interpolateRoute(
      API_ROUTES.ADMIN.THREAD_EVENTS.GET_BY_CONTACT,
      { companyId, contactId },
    )
    const response = await this.request<ApiPaginatedResponse<ThreadEvent>>(
      `${route}${queryString}`,
    )
    return { data: response.data, pagination: response.pagination }
  }

  /**
   * [ADMIN] Get a single thread event by ID for any company
   */
  async getAdminThreadEventById(eventId: string): Promise<ThreadEvent> {
    const route = interpolateRoute(API_ROUTES.ADMIN.THREAD_EVENTS.GET_BY_ID, {
      id: eventId,
    })
    const response = await this.request<ApiResponse<ThreadEvent>>(route)
    return response.data!
  }

  /**
   * [ADMIN] Get events by correlation ID for any company
   */
  async getAdminEventsByCorrelation(
    correlationId: string,
    companyId?: string,
  ): Promise<ThreadEvent[]> {
    const route = interpolateRoute(
      API_ROUTES.ADMIN.THREAD_EVENTS.GET_BY_CORRELATION,
      { correlationId },
    )
    const queryString = companyId ? `?companyId=${companyId}` : ''
    const response = await this.request<ApiResponse<ThreadEvent[]>>(
      `${route}${queryString}`,
    )
    return response.data!
  }

  /**
   * [ADMIN] Search thread events using BM25 full-text search for any company
   */
  async searchAdminThreadEvents(params: {
    q: string
    companyId?: string
    limit?: number
    directions?: string
    channels?: string
    dateFrom?: string
    dateTo?: string
    sortBy?: 'asc' | 'desc'
  }): Promise<ThreadEventSearchResponse> {
    const queryString = this.buildQueryString(params)
    return this.request<ThreadEventSearchResponse>(
      `${API_ROUTES.ADMIN.THREAD_EVENTS.SEARCH}${queryString}`,
    )
  }
}

// Export singleton instance
export const threadEventsApiClient = new ThreadEventsApiClient()

// Helper to get the thread events client (singleton instance)
export const getThreadEventsClient = () => {
  return threadEventsApiClient
}
